import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { postTurn, advance, complete, revisit } from "../api/discovery";

interface Turn {
  role: "user" | "system";
  text: string;
}

const PHASES = [1, 2, 3, 4, 5, 6, 7, 8] as const;
const PHASE_LABELS: Record<number, string> = {
  1: "Context",
  2: "Goals",
  3: "Constraints",
  4: "Stakeholders",
  5: "Capabilities",
  6: "Risks",
  7: "Plan",
  8: "Sign-off",
};

/**
 * Polished Discovery chat surface — Plan 7 Chunk 9 port of planner.jsx's
 * Discovery interview stage. Wraps the chat-only Plan 3 backend with a
 * phase indicator, transcript, advance/revisit/complete controls, and a
 * voice-mode stub. Voice mode and the Editor stage are deferred to V1.1.
 */
export function DiscoveryChat() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const workspaceId = searchParams.get("ws") ?? "";

  const [transcript, setTranscript] = useState<Turn[]>([]);
  const [input, setInput] = useState("");
  const [phase, setPhase] = useState(1);
  const [readyToAdvance, setReadyToAdvance] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revisitOpen, setRevisitOpen] = useState(false);
  const [mode, setMode] = useState<"chat" | "voice">("chat");

  async function send() {
    if (!input.trim() || !sessionId || busy) return;
    setBusy(true);
    setError(null);
    const userText = input;
    setInput("");
    setTranscript((t) => [...t, { role: "user", text: userText }]);
    try {
      const r = await postTurn(sessionId, {
        turnId: `turn-${crypto.randomUUID()}`,
        userText,
      });
      setTranscript((t) => [...t, { role: "system", text: r.assistantMessage }]);
      setReadyToAdvance(r.shouldAdvancePhase);
      if (r.session?.currentPhase) setPhase(r.session.currentPhase);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function onAdvance() {
    if (!sessionId || busy) return;
    setBusy(true);
    setError(null);
    try {
      if (phase < 8) {
        const r = await advance(sessionId);
        setPhase(r.currentPhase);
        setReadyToAdvance(false);
      } else {
        const r = await complete(sessionId);
        const draftPlay = r.draftPlay as { id?: string } | null;
        if (workspaceId && draftPlay && typeof draftPlay.id === "string") {
          navigate(`/workspaces/${workspaceId}/plays/${draftPlay.id}`);
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function onRevisit(targetPhase: number) {
    if (!sessionId || busy) return;
    setBusy(true);
    setRevisitOpen(false);
    setError(null);
    try {
      const r = await revisit(sessionId, targetPhase);
      setPhase(r.currentPhase);
      setReadyToAdvance(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="planner-discovery" data-testid="discovery-chat">
      <div className="pd-top">
        <a
          className="pd-back"
          onClick={() =>
            workspaceId
              ? navigate(`/workspaces/${workspaceId}/inbox`)
              : navigate(-1)
          }
          role="button"
          tabIndex={0}
        >
          ←
        </a>
        <div className="pd-ttl">
          Discovery session
          <span style={{ color: "var(--text-3)", marginLeft: 8 }}>
            · phase {phase} / 8
          </span>
        </div>
        <div style={{ flex: 1 }} />
        {phase < 8 ? (
          <button
            type="button"
            className="btn"
            data-testid="advance-btn"
            onClick={onAdvance}
            disabled={!readyToAdvance || busy}
          >
            Confirm phase
          </button>
        ) : (
          <button
            type="button"
            className="btn primary"
            data-testid="complete-btn"
            onClick={onAdvance}
            disabled={busy}
          >
            Complete →
          </button>
        )}
      </div>

      <div className="pd-body">
        {/* Left rail — phases + revisit + mode toggle */}
        <div className="pd-left">
          <div className="pd-mode">
            <button
              type="button"
              className={`pd-mode-btn ${mode === "voice" ? "active" : ""}`}
              onClick={() => setMode("voice")}
              data-testid="mode-voice"
            >
              <span className="pd-mode-i">◉</span>
              Voice
            </button>
            <button
              type="button"
              className={`pd-mode-btn ${mode === "chat" ? "active" : ""}`}
              onClick={() => setMode("chat")}
              data-testid="mode-chat"
            >
              <span className="pd-mode-i">✎</span>
              Text
            </button>
          </div>

          {mode === "voice" && (
            <div
              className="pd-cont"
              style={{
                background: "var(--surface-2)",
                padding: 16,
                borderRadius: 10,
              }}
              data-testid="voice-stub"
            >
              <div className="pd-cont-txt">
                <span className="pd-cont-l">Voice mode</span>
                <span className="pd-cont-s">Coming in V1.1 — use Text for now.</span>
              </div>
            </div>
          )}

          <div className="pd-prog">
            <div className="pd-prog-h">
              <span>Interview progress</span>
              <span data-testid="phase-progress">{phase} / 8</span>
            </div>
            {PHASES.map((p) => {
              const status = p < phase ? "done" : p === phase ? "active" : "todo";
              return (
                <div
                  key={p}
                  className={`pd-phase pd-phase-${status}`}
                  data-testid={`phase-${p}`}
                >
                  <span className="pd-phase-n">
                    {status === "done" ? "✓" : p}
                  </span>
                  <span className="pd-phase-l">{PHASE_LABELS[p]}</span>
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 12 }}>
            <button
              type="button"
              className="btn"
              style={{ width: "100%" }}
              data-testid="revisit-toggle"
              onClick={() => setRevisitOpen((v) => !v)}
              disabled={phase === 1 || busy}
            >
              Revisit a phase…
            </button>
            {revisitOpen && (
              <div
                style={{
                  marginTop: 6,
                  padding: 8,
                  background: "var(--surface-2)",
                  borderRadius: 8,
                }}
                data-testid="revisit-menu"
              >
                {PHASES.filter((p) => p < phase).map((p) => (
                  <button
                    type="button"
                    key={p}
                    className="btn"
                    style={{
                      width: "100%",
                      justifyContent: "flex-start",
                      marginBottom: 4,
                    }}
                    data-testid={`revisit-${p}`}
                    onClick={() => onRevisit(p)}
                  >
                    Phase {p} · {PHASE_LABELS[p]}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right canvas — transcript + composer */}
        <div className="pd-right">
          <div className="pd-canv-tabs">
            <button type="button" className="pd-canv-tab active">
              <span className="d ok" /> Transcript
            </button>
            <div style={{ flex: 1 }} />
          </div>

          <div
            className="pd-tr"
            data-testid="transcript"
            style={{ maxHeight: 480, overflow: "auto", padding: 12 }}
          >
            {transcript.length === 0 && (
              <div
                style={{ fontSize: 12, color: "var(--text-3)" }}
                data-testid="transcript-empty"
              >
                No messages yet. Say hello to start the discovery interview.
              </div>
            )}
            {transcript.map((t, i) => (
              <div
                key={i}
                className={`pd-msg ${t.role === "user" ? "pd-msg-you" : "pd-msg-xami"}`}
              >
                <div className="pd-msg-h">
                  <span className="pd-msg-w">
                    {t.role === "user" ? "YOU" : "XAMI"}
                  </span>
                </div>
                <div className="pd-msg-b">{t.text}</div>
              </div>
            ))}
          </div>

          {mode === "chat" && (
            <div style={{ padding: 12, borderTop: "1px solid var(--border)" }}>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your reply..."
                rows={3}
                className="w-full"
                style={{
                  width: "100%",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  padding: 8,
                  fontSize: 13,
                  fontFamily: "inherit",
                  resize: "vertical",
                }}
                data-testid="composer"
              />
              <div
                style={{
                  marginTop: 8,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <button
                  type="button"
                  className="btn primary"
                  onClick={send}
                  disabled={busy || !input.trim()}
                  data-testid="send-btn"
                >
                  Send
                </button>
                {error && (
                  <span
                    role="alert"
                    style={{ color: "var(--danger)", fontSize: 12 }}
                  >
                    {error}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Backwards-compat phase indicator for legacy tests */}
      <div
        data-testid="phase-indicator"
        aria-label={`Phase ${phase} of 8`}
        style={{ position: "absolute", left: -9999 }}
      >
        Phase {phase} / 8
      </div>
    </div>
  );
}
